// Page de choix de formule d'abonnement (après onboarding + questionnaire).
// L'utilisateur ne peut pas accéder au tableau de bord sans avoir choisi une formule.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatErreur } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Loader2, Check, Sparkles } from "lucide-react";

const fmt = (n) => Math.round(n || 0).toLocaleString("fr-FR");

const ATOUTS = {
  solo: ["TPE 1 à 5 personnes", "1 utilisateur", "Tous les journaux & états", "Exports CSV / XLS / PDF"],
  equipe: ["PME 6 à 20 personnes", "Jusqu'à 5 utilisateurs", "Tout Solo inclus", "Support prioritaire"],
  structure: ["PME 20+", "Utilisateurs illimités", "Multi-sociétés", "Tout Équipe inclus"],
};

export default function Formules() {
  const navigate = useNavigate();
  const { chargerAbonnement } = useAuth();
  const [config, setConfig] = useState(null);
  const [periodicite, setPeriodicite] = useState("mensuel");
  const [enCours, setEnCours] = useState(null);

  useEffect(() => {
    api.get("/abonnements/formules").then(({ data }) => setConfig(data)).catch(() => setConfig(null));
  }, []);

  const choisir = async (code) => {
    setEnCours(code);
    try {
      await api.post("/abonnements/souscrire", { formule: code, periodicite });
      await chargerAbonnement();
      toast.success("Votre période d'essai de 30 jours est activée. Bienvenue !");
      navigate("/tableau-de-bord");
    } catch (e) {
      toast.error(formatErreur(e.response?.data?.detail));
    } finally {
      setEnCours(null);
    }
  };

  if (!config) {
    return <div className="min-h-screen flex items-center justify-center bg-[#E8F4F4]"><Loader2 className="h-8 w-8 animate-spin text-[#1D3D3D]" /></div>;
  }

  return (
    <div data-testid="page-formules" className="min-h-screen bg-[#E8F4F4] py-12 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1D3D3D] text-white text-xs font-semibold">
            <Sparkles className="h-3.5 w-3.5" /> {config.trial_jours} jours d'essai gratuit · sans carte bancaire
          </div>
          <h1 className="mt-4 text-4xl font-black tracking-tight text-[#1D3D3D]">Choisissez votre formule</h1>
          <p className="mt-2 text-[#1D3D3D]/70">Tarifs en XPF · Polynésie française · résiliable à tout moment</p>
        </div>

        {/* Bascule mensuel / annuel */}
        <div className="mt-8 flex justify-center">
          <div className="inline-flex bg-white rounded-full p-1 border border-[#4A8A8A]/30">
            <button data-testid="toggle-mensuel" onClick={() => setPeriodicite("mensuel")}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${periodicite === "mensuel" ? "bg-[#1D3D3D] text-white" : "text-[#1D3D3D]"}`}>Mensuel</button>
            <button data-testid="toggle-annuel" onClick={() => setPeriodicite("annuel")}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${periodicite === "annuel" ? "bg-[#1D3D3D] text-white" : "text-[#1D3D3D]"}`}>Annuel <span className="text-emerald-500">-10%</span></button>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          {config.formules.map((f) => {
            const populaire = f.code === "equipe";
            return (
              <div key={f.code} data-testid={`formule-${f.code}`}
                className={`relative bg-white rounded-2xl border-2 p-6 flex flex-col ${populaire ? "border-[#1D3D3D] shadow-lg" : "border-[#4A8A8A]/20"}`}>
                {populaire && <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-[#1D3D3D] text-white text-xs font-semibold">Le plus choisi</span>}
                <h2 className="text-lg font-black text-[#1D3D3D] uppercase tracking-wide">{f.libelle}</h2>
                <p className="mt-1 text-sm text-[#1D3D3D]/60 min-h-[40px]">{f.description}</p>
                <div className="mt-4">
                  <span data-testid={`prix-${f.code}`} className="text-3xl font-black text-[#1D3D3D]">{fmt(f.prix[periodicite])}</span>
                  <span className="text-sm font-medium text-[#1D3D3D]/70"> XPF / {periodicite === "mensuel" ? "mois" : "an"}</span>
                </div>
                <ul className="mt-5 space-y-2 flex-1">
                  {(ATOUTS[f.code] || []).map((a) => (
                    <li key={a} className="flex items-start gap-2 text-sm text-[#1D3D3D]"><Check className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> {a}</li>
                  ))}
                </ul>
                <Button data-testid={`choisir-${f.code}`} onClick={() => choisir(f.code)} disabled={enCours === f.code}
                  className={`mt-6 h-11 font-semibold ${populaire ? "bg-[#1D3D3D] hover:bg-[#2D5A5A] text-white" : "bg-[#E8F4F4] hover:bg-[#4A8A8A]/20 text-[#1D3D3D] border border-[#4A8A8A]/30"}`}>
                  {enCours === f.code ? <Loader2 className="h-4 w-4 animate-spin" /> : "Choisir cette formule"}
                </Button>
              </div>
            );
          })}
        </div>
        <p className="mt-6 text-center text-xs text-[#1D3D3D]/60">
          Aucune carte bancaire requise pour l'essai. À l'issue des 30 jours, renseignez votre moyen de paiement pour continuer.
        </p>
      </div>
    </div>
  );
}
